import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { downloadFile, formatErr, STATUS_STYLES } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, FileDown, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function AuditReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [actions, setActions] = useState([]);

  useEffect(() => {
    api.get(`/audits/${id}`).then((r) => setAudit(r.data)).catch((e) => toast.error(formatErr(e)));
    api.get("/actions").then((r) => setActions(r.data.filter((a) => a.audit_id === id)));
  }, [id]);

  if (!audit)
    return <div className="flex justify-center py-24"><div className="w-8 h-8 border-2 border-[#14532D] border-t-transparent rounded-full animate-spin" /></div>;

  const scores = audit.scores || { sections: [] };
  const overall = scores.overall_score;
  const scoreColor = overall == null ? "text-stone-400" : overall >= 85 ? "text-emerald-600" : overall >= 70 ? "text-amber-500" : "text-rose-600";

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="audit-report-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/audits")}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-stone-900">{audit.template_name}</h1>
            <p className="text-sm text-stone-500">{audit.hotel_name} · {audit.auditor_name} · {audit.submitted_at?.slice(0, 10)}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" data-testid="export-pdf-button" onClick={() => downloadFile(`/reports/audit/${id}/pdf`, `audit-report-${id.slice(0, 8)}.pdf`).catch(() => toast.error("Export failed"))}>
            <FileDown className="w-4 h-4 mr-2" />PDF
          </Button>
          <Button variant="outline" data-testid="export-excel-button" onClick={() => downloadFile(`/reports/audit/${id}/excel`, `audit-report-${id.slice(0, 8)}.xlsx`).catch(() => toast.error("Export failed"))}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white border border-[#E7E5DF] rounded-xl p-8 flex flex-col items-center justify-center">
          <p className="text-xs font-medium uppercase tracking-wider text-stone-500">Overall Score</p>
          <p className={`text-6xl font-extrabold tracking-tight mt-2 ${scoreColor}`} style={{ fontFamily: "Manrope" }} data-testid="overall-score">
            {overall != null ? `${overall}%` : "—"}
          </p>
          {scores.critical_failure && (
            <p className="mt-3 flex items-center gap-1.5 text-xs font-bold text-rose-600 uppercase"><AlertTriangle className="w-4 h-4" />Critical failure</p>
          )}
        </div>
        <div className="md:col-span-2 bg-white border border-[#E7E5DF] rounded-xl p-6">
          <h3 className="font-semibold text-stone-800 mb-4">Section Scores</h3>
          <div className="space-y-3" data-testid="section-scores">
            {scores.sections.map((s) => (
              <div key={s.id} className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between text-sm">
                    <span className="truncate text-stone-700">{s.title} {s.critical_failed && <span className="text-rose-600 text-[10px] font-bold uppercase ml-1">critical fail</span>}</span>
                    <span className="font-mono font-semibold">{s.score != null ? `${s.score}%` : "N/A"}</span>
                  </div>
                  <div className="h-1.5 bg-stone-100 rounded-full mt-1">
                    <div className={`h-1.5 rounded-full ${s.critical_failed || (s.score ?? 100) < 70 ? "bg-rose-500" : (s.score ?? 100) < 85 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${s.score ?? 0}%` }} />
                  </div>
                </div>
                <span className="text-[11px] font-mono text-stone-400 w-24 text-right">{s.earned}/{s.max} pts</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E7E5DF] rounded-xl p-6">
        <h3 className="font-semibold text-stone-800 mb-4">Corrective Actions Raised ({actions.length})</h3>
        {actions.length === 0 ? (
          <p className="text-sm text-stone-400 py-6 text-center">No non-compliances — clean audit.</p>
        ) : (
          <div className="overflow-x-auto"><table className="w-full min-w-[680px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
                <th className="pb-2 pr-3">Finding</th>
                <th className="pb-2 pr-3">Department</th>
                <th className="pb-2 pr-3">Responsible</th>
                <th className="pb-2 pr-3">Priority</th>
                <th className="pb-2 pr-3">Due</th>
                <th className="pb-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((a) => (
                <tr key={a.id} className="border-b border-stone-100 last:border-0 cursor-pointer hover:bg-stone-50/60" onClick={() => navigate("/actions")}>
                  <td className="py-3 pr-3 text-sm text-stone-800 max-w-[280px]">{a.description}</td>
                  <td className="py-3 pr-3 text-sm text-stone-600">{a.department}</td>
                  <td className="py-3 pr-3 text-sm text-stone-600">{a.responsible_name}</td>
                  <td className="py-3 pr-3">
                    <span className={`text-[11px] font-semibold uppercase ${a.priority === "high" ? "text-rose-600" : a.priority === "medium" ? "text-amber-600" : "text-stone-500"}`}>{a.priority}</span>
                  </td>
                  <td className="py-3 pr-3 text-sm font-mono text-stone-600">{a.due_date?.slice(0, 10)}</td>
                  <td className="py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_STYLES[a.status]}`}>{a.status.replace("_", " ")}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
